import React, { useCallback, useMemo, useState } from 'react';
import { JRCButtonDropdown } from '../JRCButtonDropdown/JRCButtonDropdown';
import { useResponseStatus } from './useResponseStatus';
import { JGlobalApi, UserResponseStatus } from 'jamespot-user-api';
import { JRCLoader } from '../JRCLoader/JRCLoader';
import { JRCButtonDropdownOptionProps } from '../JRCButtonDropdown/JRCButtonDropdown.types';
import { useAbortController } from '../../hooks/useAbortController';
import { useIntl } from 'react-intl';
import { getLightShade } from '../../styles/utils';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import styled, { useTheme } from 'styled-components';
import { Colors } from '../../styles/theme';

export type SubscriptionModifiers = { uri: string; modifier: number; status: UserResponseStatus }[];

export type JRCEventStatusButtonProps = {
    eventId: number;
    eventType: string;
    initialStatus?: UserResponseStatus | null;
    eventIsVisio?: boolean;
    onUpdateStatus?: (status: UserResponseStatus) => void;
    onError: (errorMessage: string) => void;
    onSuccess: (message: string) => void;
    handler: {
        global: Pick<JGlobalApi, 'updateAction'>;
    };
    canSubscribe?: boolean;
    setSubscriptionModifiers: (fn: (prev: SubscriptionModifiers) => SubscriptionModifiers) => void;
    eventUri: string;
    status: UserResponseStatus | null;
};

const StatusIndicator = styled.button<{ bg: string; hover: string }>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.sm}px;
    padding: 0 ${({ theme }) => theme.space.md}px;
    border-radius: ${({ theme }) => theme.space.sm}px;
    background-color: ${({ bg }) => bg};
    height: 40px;
    margin: 0.2rem;
    border: none;

    &:hover {
        cursor: pointer;
        background-color: ${({ hover }) => hover};
    }
`;

const StatusDot = styled.div<{ color: Colors }>`
    height: 8px;
    width: 8px;
    border-radius: 100%;
    background-color: ${({ color, theme }) => theme.color[color]};
`;

export const JRCEventStatusButton = ({
    onError,
    handler,
    onSuccess,
    eventId,
    eventUri,
    eventType,
    eventIsVisio,
    initialStatus,
    canSubscribe = true,
    setSubscriptionModifiers,
    status,
}: JRCEventStatusButtonProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const { getStatusColor, getStatusLabel } = useResponseStatus();
    const abortController = useAbortController();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<'display' | 'edition'>('display');
    const color = useMemo(() => getStatusColor(status ?? 'available'), [getStatusColor, status]);
    const label = useMemo(() => getStatusLabel(status ?? 'available'), [getStatusLabel, status]);

    const updateStatus = useCallback(
        (status: UserResponseStatus) => {
            setLoading(true);
            handler.global
                .updateAction<UserResponseStatus>(
                    {
                        targetId: eventId,
                        targetType: eventType,
                        type: 'socialEventSubscribe',
                        value: status,
                    },
                    { signal: abortController.signal },
                )
                .then(() => {
                    onSuccess(intl.formatMessage({ id: 'GLOBAL_Successfully_Saved' }));
                    if (
                        (status === 1 || status === 'onSpot' || status === 'visio' || status === 'available') &&
                        (initialStatus === 0 || initialStatus === 'unavailable' || initialStatus === 'unknown')
                    ) {
                        setSubscriptionModifiers((prev) => [
                            ...prev.filter((e) => e.uri !== eventUri),
                            {
                                uri: eventUri,
                                modifier: 1,
                                status,
                            },
                        ]);
                    } else if (
                        (status === 0 || status === 'unavailable' || status === 'unknown') &&
                        (initialStatus === 1 ||
                            initialStatus === 'available' ||
                            initialStatus === 'onSpot' ||
                            initialStatus === 'visio')
                    ) {
                        setSubscriptionModifiers((prev) => [
                            ...prev.filter((e) => e.uri !== eventUri),
                            {
                                uri: eventUri,
                                modifier: -1,
                                status,
                            },
                        ]);
                    } else {
                        setSubscriptionModifiers((prev) => [
                            ...prev.filter((e) => e.uri !== eventUri),
                            {
                                uri: eventUri,
                                modifier: 0,
                                status,
                            },
                        ]);
                    }
                })
                .catch((e) => {
                    onError(e.errorMsg);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [
            abortController.signal,
            eventId,
            eventType,
            eventUri,
            handler.global,
            initialStatus,
            intl,
            onError,
            onSuccess,
            setSubscriptionModifiers,
        ],
    );

    const options = useMemo<Array<JRCButtonDropdownOptionProps>>(() => {
        const ops: Array<JRCButtonDropdownOptionProps> = [];
        if (eventIsVisio) {
            ops.push({
                label: intl.formatMessage({ id: 'CALENDAR_Status_OnSpot' }),
                onClick: () => updateStatus('onSpot'),
                disabled: !canSubscribe,
            });
            ops.push({
                label: intl.formatMessage({ id: 'CALENDAR_Status_Visio' }),
                onClick: () => updateStatus('visio'),
                disabled: !canSubscribe,
            });
        } else {
            ops.push({
                label: intl.formatMessage({ id: 'CALENDAR_Join' }),
                onClick: () => updateStatus(1),
                disabled: !canSubscribe,
            });
        }
        ops.push({
            label: intl.formatMessage({ id: 'CALENDAR_Reject' }),
            onClick: () => updateStatus(0),
        });
        return ops;
    }, [canSubscribe, eventIsVisio, intl, updateStatus]);

    if (mode === 'edition') {
        return (
            <>
                {loading && <JRCLoader size={'s'} />}
                <JRCButtonDropdown
                    disabled={loading}
                    label={intl.formatMessage({ id: 'CALENDAR_Answer' })}
                    options={options}
                    onClose={() => setMode('display')}
                    defaultOpen
                />
            </>
        );
    } else {
        return (
            <StatusIndicator
                data-cy={'event-status-button'}
                onClick={(e) => {
                    e.stopPropagation();
                    setMode('edition');
                }}
                bg={getLightShade(theme.color[color], 0.9)}
                hover={getLightShade(theme.color[color], 0.8)}>
                <StatusDot color={color} />
                <JRCTypography>{label}</JRCTypography>
            </StatusIndicator>
        );
    }
};
